import { useContext } from 'react';
import { alpha } from '@mui/material/styles';
import { ThemeContext } from 'src/store/ContextStore';
import { REFRESH_COLOURS } from '../../SidebarSection';

// The panel's controls wear the SAME treatment as the sidebar accordion rows they
// sit under — light and neutral at rest, a faint accent tint on hover with the
// accent applied to the glyph. Both sets of values come from SidebarSection so the
// panel cannot drift from the row above it.
//
// This deliberately replaced a first attempt that filled the tiles with
// `palette.secondary` (the configured button colour). On a deployment whose button
// colour is a dark navy that produced a wall of dark blocks — far too heavy for a
// panel of fifteen controls, and it made the tiles compete with the pedigree
// itself.

/** Faint tint used for the accordion's hover AND expanded row. */
export const TINT_ALPHA = 0.07;

/**
 * Brand teal fails the 4.5:1 floor against white (about 2.9:1), so anything wearing
 * it as a foreground — the selected chip's white text, the hovered label, the
 * hovered glyph — uses the darkened teal instead, exactly as SidebarSection does for
 * its active label. The brand teal is still the tint and the focus ring, where it is
 * decoration rather than information.
 */
export const ON_ACCENT_SAFE = REFRESH_COLOURS.labelActive;

export const DEFAULT_ACCENT = '#2ba9bf';

/** Resting / hover colours for a panel control, derived from `theme.accent`. */
export const tileColours = (accent = DEFAULT_ACCENT) => ({
  accent,
  // Resting label and glyph: the accordion's navy, which is what the mock shows
  // and what keeps a thin pedigree stroke legible (the accordion's own mid-grey
  // icon colour is tuned for a filled 24px MUI glyph, not a 1.75px outline).
  restText: REFRESH_COLOURS.label,
  // Hover: the DARKENED teal on both the label and the glyph.
  //
  // SidebarSection puts the brand teal on its icons and reserves the darkened teal
  // for its small label, because a 24px filled MUI glyph has contrast to spare. A
  // pedigree symbol is a 1.75px outline, and the brand teal only manages about
  // 2.9:1 on the tint — under the 3:1 that WCAG 1.4.11 asks of a non-text UI
  // component. The darkened teal clears it at 5.7:1 and is what Tim asked for.
  hoverText: REFRESH_COLOURS.labelActive,
  hoverGlyph: REFRESH_COLOURS.labelActive,
  hoverTint: alpha(accent, TINT_ALPHA),
  hoverBorder: alpha(accent, 0.5),
  // The child-scope container: a whisper of the same accent, so the box reads as a
  // scope rather than as a grey smear behind the tiles.
  scopeTint: alpha(accent, 0.04),
  scopeBorder: alpha(accent, 0.22),
  // Solid fill for the selected spouse chip — the darkened teal, so white text on
  // it clears 4.5:1.
  selectedFill: ON_ACCENT_SAFE
});

/** Hook form, for components that render inside the sidebar's ThemeContext. */
export const useTileColours = () => {
  const { theme } = useContext(ThemeContext) || {};
  return tileColours(theme?.accent || DEFAULT_ACCENT);
};
